import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, CircleSlash, Download, ExternalLink, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, EmptyState, ErrorBox, Loading, Modal, Notice, Pill, Select, td, th, useToast, type Tone } from './ui';
import { ScrollTable } from './virtual';
import { saveFile } from '../lib/download';
import { dt, num } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { rpc } from '../lib/rpc';

/**
 * Verwaltung › Berichte (request 32 of round 4): one report per night (the steps of the maintenance with their result)
 * and the findings of the daily check, which looks for problems that arose in the background. Each report and the list
 * can be exported as Excel; "Jetzt prüfen" runs the check at once. The reports hold counts and project numbers only.
 */

const STATUS_TONE: Record<string, Tone> = { ok: 'good', warning: 'warn', error: 'bad', running: 'info', skipped: 'neutral' };

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const icon =
    status === 'ok' ? <CheckCircle2 className="size-3" /> : status === 'warning' ? <AlertTriangle className="size-3" /> : status === 'error' ? <XCircle className="size-3" /> : status === 'skipped' ? <CircleSlash className="size-3" /> : null;
  return (
    <Pill tone={STATUS_TONE[status] ?? 'neutral'}>
      {icon}
      {t(`jobs.status.${status as 'ok'}`, { defaultValue: status })}
    </Pill>
  );
}

export function JobReportsTab() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [kind, setKind] = useState<'' | 'nightly' | 'check'>('');
  const q = useQ('jobs.list', { limit: 120, kind: kind || null });
  const check = useM('jobs.checkNow', { invalidate: ['jobs.', 'notifications.'] });
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lang = (['de', 'en', 'tr'] as const).find((l) => i18n.language.startsWith(l)) ?? 'de';
  const exportFile = async (id?: string) => {
    setBusy(true);
    try {
      saveFile(await rpc('jobs.export', { id: id ?? null, lang }));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const latest = q.data?.runs[0];
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="neutral">{t('jobs.intro')}</Notice>
      <div className="flex flex-wrap items-center gap-2">
        <Select className="w-48" value={kind} onChange={(e) => setKind(e.target.value as 'nightly')} aria-label={t('jobs.kind')}>
          <option value="">{t('jobs.kinds.all')}</option>
          <option value="nightly">{t('jobs.kinds.nightly')}</option>
          <option value="check">{t('jobs.kinds.check')}</option>
        </Select>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            icon={<RefreshCw className="size-4" />}
            loading={check.isPending}
            onClick={() =>
              check
                .mutateAsync({})
                .then((r) => {
                  toast.ok(t('jobs.checked', { count: r.findings.length }));
                  setOpen(r.id);
                })
                .catch(toast.error)
            }
          >
            {t('jobs.checkNow')}
          </Button>
          <Button icon={<Download className="size-4" />} loading={busy} onClick={() => void exportFile()}>
            {t('jobs.exportList')}
          </Button>
        </div>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {latest && latest.status !== 'ok' && (
        <Notice tone={latest.status === 'error' ? 'bad' : 'warn'}>
          {t('jobs.latestProblem', { date: dt(latest.startedAt), errors: latest.errors, warnings: latest.warnings })}{' '}
          <button type="button" className="underline underline-offset-2" onClick={() => setOpen(latest.id)}>
            {t('jobs.open')}
          </button>
        </Notice>
      )}
      {q.data && q.data.runs.length === 0 && <EmptyState>{t('jobs.none')}</EmptyState>}
      {!!q.data?.runs.length && (
        <ScrollTable label={t('admin.tabs.jobs')}>
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('jobs.started')}</th>
                <th className={th}>{t('jobs.kind')}</th>
                <th className={th}>{t('common.status')}</th>
                <th className={clsx(th, 'text-right')}>{t('jobs.steps')}</th>
                <th className={clsx(th, 'text-right')}>{t('jobs.duration')}</th>
                <th className={th}>{t('jobs.findings')}</th>
                <th className={clsx(th, 'w-24')} />
              </tr>
            </thead>
            <tbody>
              {q.data.runs.map((r) => {
                const ms = r.finishedAt ? Date.parse(r.finishedAt) - Date.parse(r.startedAt) : null;
                return (
                  <tr key={r.id} className="cursor-pointer border-b border-line/70 hover:bg-sheet-2" onClick={() => setOpen(r.id)}>
                    <td className={clsx(td, 'num whitespace-nowrap')}>{dt(r.startedAt)}</td>
                    <td className={td}>{t(`jobs.kinds.${r.kind as 'nightly'}`, { defaultValue: r.kind })}</td>
                    <td className={td}>
                      <StatusPill status={r.status} />
                    </td>
                    <td className={clsx(td, 'num text-right')}>
                      {r.steps.length}
                      {r.failedSteps > 0 && <span className="ml-1 text-bad">({t('jobs.failedCount', { count: r.failedSteps })})</span>}
                    </td>
                    <td className={clsx(td, 'num text-right')}>{ms === null ? '-' : `${num(Math.round(ms / 100) / 10)} s`}</td>
                    <td className={td}>
                      {r.findings.length === 0 ? (
                        <span className="text-xs text-graphite">{t('jobs.noFindings')}</span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {r.findings.slice(0, 4).map((f) => (
                            <Pill key={f.code} tone={f.severity === 'error' ? 'bad' : f.severity === 'warning' ? 'warn' : 'info'}>
                              {t(`jobs.codes.${f.code}.short`, { defaultValue: f.code })} {f.count > 1 ? `(${f.count})` : ''}
                            </Pill>
                          ))}
                          {r.findings.length > 4 && <span className="text-xs text-graphite">+{r.findings.length - 4}</span>}
                        </span>
                      )}
                    </td>
                    <td className={clsx(td, 'text-right')}>
                      <Button size="sm" variant="ghost" onClick={(e) => (e.stopPropagation(), void exportFile(r.id))} icon={<Download className="size-3.5" />}>
                        Excel
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollTable>
      )}
      <ReportDialog id={open} onClose={() => setOpen(null)} onExport={(id) => void exportFile(id)} busy={busy} />
    </div>
  );
}

function ReportDialog({ id, onClose, onExport, busy }: { id: string | null; onClose: () => void; onExport: (id: string) => void; busy: boolean }) {
  const { t } = useTranslation();
  const q = useQ('jobs.get', { id: id ?? '' }, { enabled: !!id });
  const r = q.data;
  return (
    <Modal
      open={!!id}
      onClose={onClose}
      wide
      title={r ? t('jobs.reportTitle', { date: dt(r.startedAt), kind: t(`jobs.kinds.${r.kind as 'nightly'}`, { defaultValue: r.kind }) }) : t('jobs.report')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          {r && (
            <Button variant="primary" icon={<Download className="size-4" />} loading={busy} onClick={() => onExport(r.id)}>
              {t('jobs.exportOne')}
            </Button>
          )}
        </>
      }
    >
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {r && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <StatusPill status={r.status} />
            <span className="text-graphite">{t('jobs.summary', { steps: r.steps.length, errors: r.errors, warnings: r.warnings })}</span>
          </div>
          <div>
            <h3 className="mb-1 text-sm font-semibold">{t('jobs.findings')}</h3>
            {r.findings.length === 0 ? (
              <p className="text-sm text-good">{t('jobs.noFindingsLong')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {r.findings.map((f) => (
                  <li key={f.code} className={clsx('rounded-md border px-3 py-2 text-sm', f.severity === 'error' ? 'border-bad/30 bg-bad-soft' : f.severity === 'warning' ? 'border-warn/30 bg-warn-soft' : 'border-line bg-sheet-2')}>
                    <span className="font-medium">{t(`jobs.codes.${f.code}.short`, { defaultValue: f.code })}</span>
                    <span className="ml-2 text-graphite">{t(`jobs.codes.${f.code}.text`, { ...f.params, count: f.count, defaultValue: '' })}</span>
                    {f.link && (
                      <Link to={f.link} onClick={onClose} className="ml-2 inline-flex items-center gap-1 text-xs text-accent hover:underline">
                        {t('jobs.lookAt')} <ExternalLink className="size-3" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="mb-1 text-sm font-semibold">{t('jobs.steps')}</h3>
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('jobs.step')}</th>
                  <th className={th}>{t('common.status')}</th>
                  <th className={clsx(th, 'text-right')}>{t('jobs.duration')}</th>
                  <th className={th}>{t('jobs.result')}</th>
                </tr>
              </thead>
              <tbody>
                {r.steps.map((s, i) => (
                  <tr key={`${s.name}-${i}`} className="border-b border-line/60 align-top">
                    <td className={td}>{t(`jobs.stepNames.${s.name}`, { defaultValue: s.name })}</td>
                    <td className={td}>
                      <StatusPill status={s.status} />
                    </td>
                    <td className={clsx(td, 'num text-right whitespace-nowrap')}>{num(Math.round(s.ms / 100) / 10)} s</td>
                    <td className={clsx(td, 'text-xs break-all text-graphite')}>
                      {s.error ? <span className="text-bad">{s.error}</span> : s.summary}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
